import { apiGet, apiGetMeta, apiPut } from './api-client.js';

/**
 * Typed wrappers over API_CONTRACT.md §13.5's four availability endpoints
 * (`internal-api`'s `availability` module). No `VERIFIED_RESPONSES.md` entry exists yet;
 * shapes below were read off the running server (`availability-repository.ts` /
 * `availability-routes.ts`), not inferred from prose.
 *
 * **`GET /reference/availability-statuses` (API_CONTRACT §13.9) is not implemented** —
 * confirmed live (`404 NOT_FOUND`), and no `reference` module exists in
 * `internal-api/routes.ts` at all: `identity`, `file`, `workflow`, `notification`,
 * `availability`, `appointment` are the only modules mounted. `file-lifecycle.ts` already
 * carries this exact stopgap for confidentiality levels and reassignment reasons —
 * `AVAILABILITY_STATUSES` below is the same pattern, transcribed from the exact seed
 * migration (`api/drizzle/0016_batch11_availability.sql`'s `INSERT INTO
 * availability_status`) rather than guessed. It DRIFTS the moment a Super Admin edits the
 * table (there is no admin screen for it in this build, so today that risk is theoretical),
 * flagged in the handback as a finding, not shipped as a permanent design.
 */
export interface AvailabilityStatusInfo {
  code: string;
  rank: number;
  labelNe: string;
  labelEn: string;
  publicLabelNe: string;
  publicLabelEn: string;
  isAvailableHint: boolean;
  isDefault: boolean;
}

export const AVAILABILITY_STATUSES: readonly AvailabilityStatusInfo[] = [
  {
    code: 'available',
    rank: 10,
    labelNe: 'उपलब्ध',
    labelEn: 'Available',
    publicLabelNe: 'उपलब्ध',
    publicLabelEn: 'Available',
    isAvailableHint: true,
    isDefault: false,
  },
  {
    code: 'in_meeting',
    rank: 20,
    labelNe: 'बैठकमा',
    labelEn: 'In a meeting',
    publicLabelNe: 'अहिले उपलब्ध छैन',
    publicLabelEn: 'Not available right now',
    isAvailableHint: false,
    isDefault: false,
  },
  {
    code: 'on_leave',
    rank: 30,
    labelNe: 'बिदामा',
    labelEn: 'On leave',
    publicLabelNe: 'अहिले उपलब्ध छैन',
    publicLabelEn: 'Not available right now',
    isAvailableHint: false,
    isDefault: false,
  },
  {
    code: 'out_of_office',
    rank: 40,
    labelNe: 'कार्यालय बाहिर',
    labelEn: 'Out of office',
    publicLabelNe: 'अहिले उपलब्ध छैन',
    publicLabelEn: 'Not available right now',
    isAvailableHint: false,
    isDefault: false,
  },
  {
    code: 'not_published',
    rank: 50,
    labelNe: 'तोकिएको छैन',
    labelEn: 'Not set',
    publicLabelNe: 'जानकारी उपलब्ध छैन',
    publicLabelEn: 'Not published',
    isAvailableHint: false,
    isDefault: true,
  },
];

export function availabilityStatusLabel(code: string, locale: 'en' | 'ne'): string {
  const entry = AVAILABILITY_STATUSES.find((s) => s.code === code);
  if (!entry) return code;
  return locale === 'ne' ? entry.labelNe : entry.labelEn;
}

/** What a CITIZEN sees for this status — shown in this UI only as a preview label, never editable here (AC-19: no location, no fine timestamp in the public projection). */
export function availabilityPublicLabel(code: string, locale: 'en' | 'ne'): string {
  const entry = AVAILABILITY_STATUSES.find((s) => s.code === code);
  if (!entry) return code;
  return locale === 'ne' ? entry.publicLabelNe : entry.publicLabelEn;
}

export interface AvailabilityStatusValue {
  code: string;
  labelNe: string;
  labelEn: string;
  isAvailableHint: boolean;
}

export interface MyAvailability {
  userId: string;
  status: AvailabilityStatusValue;
  internalNote: string | null;
  source: string;
  version: number;
}

export function getMyAvailability(signal?: AbortSignal): Promise<MyAvailability> {
  return apiGet<MyAvailability>('/me/availability', signal);
}

export interface UpdateAvailabilityInput {
  statusCode: string;
  internalNote?: string | null;
  expectedVersion: number;
}

export function updateMyAvailability(input: UpdateAvailabilityInput): Promise<MyAvailability> {
  return apiPut<MyAvailability>('/me/availability', input);
}

export interface StaffAvailabilityRow {
  userId: string;
  fullName: string;
  /**
   * R-051: both are NULLABLE, and both were declared non-null here while the server has
   * always answered `| null` (`availability-service.ts`'s `AvailabilityBoardEntry`).
   * `apiGetMeta<T>` is an unchecked cast, so TypeScript validated nothing and
   * `row.designation.labelNe` threw the moment a staff member had no designation —
   * which is every account the bootstrap creates and every account the admin UI creates,
   * since neither sets one. That took out the whole Availability Administration screen
   * with the error boundary's "Something went wrong".
   */
  designation: { labelNe: string; labelEn: string } | null;
  orgUnit: { id: string; nameNe: string; nameEn: string } | null;
  status: AvailabilityStatusValue;
  internalNote: string | null;
  source: string;
  version: number;
}

export interface ListAvailabilityParams {
  page?: number;
  pageSize?: number;
}

export async function listAvailability(
  params: ListAvailabilityParams = {},
  signal?: AbortSignal,
): Promise<{ rows: StaffAvailabilityRow[]; page: { page: number; pageSize: number; totalItems: number; totalPages: number } }> {
  const usp = new URLSearchParams();
  if (params.page !== undefined) usp.set('page', String(params.page));
  if (params.pageSize !== undefined) usp.set('pageSize', String(params.pageSize));
  const qs = usp.toString();
  const { data, meta } = await apiGetMeta<StaffAvailabilityRow[]>(`/availability${qs ? `?${qs}` : ''}`, signal);
  const page =
    (meta?.page as { page: number; pageSize: number; totalItems: number; totalPages: number } | undefined) ?? {
      page: 1,
      pageSize: 25,
      totalItems: data.length,
      totalPages: 1,
    };
  return { rows: data, page };
}

/**
 * `PUT /users/{id}/availability` — HR override. "Audited with actor ≠ subject" (§13.5).
 * Verified live: the response is the SAME thin shape as `PUT /me/availability`
 * (`{userId, status, internalNote, source, version}`) — no `fullName`/`designation`/
 * `orgUnit` — so the caller re-reads those from the row it already has in the admin table
 * rather than expecting this response to carry them.
 */
export function updateUserAvailability(userId: string, input: UpdateAvailabilityInput): Promise<MyAvailability> {
  return apiPut<MyAvailability>(`/users/${userId}/availability`, input);
}

export { ApiError, NetworkError } from './api-client.js';
